import React from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';

import CCEvents, { EventNames } from '@companycam/companycam-events';

import { LD_SECURE_UPLOADS_REQUESTS_WEB_FLAG } from 'constants/featureFlags';
import { IMAGE_MAX_SIZE_BYTES as maxFileUploadSizeBytes } from 'constants/files';

import { fileIsPhoto } from 'helpers/fileIsPhoto';
import { uploadFileAndCreateProjectImageV1 } from 'helpers/uploadFileAndCreateProjectImageV1';
import { uploadRequestAndCreateProjectPhoto } from 'helpers/uploadRequestAndCreateProjectPhoto';
import toast from 'helpers/toast';

import { useCurrentUser } from 'javascripts/hooks';

import DragAndDropUploader from './DragAndDropUploader';
import Modal from 'components/shared/DesignSystem/Modal';
import { useAddTaskImages } from 'components/projects/taskList/hooks';

// #region styled-components
const StyledModal = styled(Modal)`
  width: 600px;
  margin: 0 8px;
  border-radius: 4px;
  position: relative;
`;

// #endregion

const messages = defineMessages({
  addPhotoError: {
    id: 'Projects.taskList.taskPhotoUploadModal.addPhotoError',
    defaultMessage: 'There was an error adding the photo to the field',
    description: 'message displayed when an error occurs adding photo to task',
  },
  photosFinished: {
    id: 'Projects.taskList.taskPhotoUploadModal.photosFinished',
    defaultMessage: `Your photos have finished uploading and are now processing. They will appear shortly in your project and field. 
    To continue adding photos, click here or drag & drop additional photos`,
    description: 'message displayed when an photos have finished uploading',
  },
  uploadPhoto: {
    id: 'Projects.taskList.taskPhotoUploadModal.uploadPhoto',
    defaultMessage: 'Upload Photos',
    description: 'title displayed for uploading photos to a task',
  },
  addPhotosSuccess: {
    id: 'Projects.taskList.taskPhotoUploadModal.addPhotosSuccess',
    defaultMessage: 'Photos added',
    description: 'message displayed when photos are added successfully',
  },
});
const NOOP = () => {};

const TaskPhotoUploadModal = ({
  isOpen = false,
  onRequestClose = NOOP,
  taskId,
}) => {
  const intl = useIntl();
  const { id: projectId } = useParams();

  const currentUser = useCurrentUser();

  const flags = useFlags();
  const secureUploadFlagEnabled = flags[LD_SECURE_UPLOADS_REQUESTS_WEB_FLAG];

  const { addTaskImages } = useAddTaskImages({
    onCompleted: () => {
      CCEvents.logEvent(EventNames.TASK_LISTS.TAKE_TASK_PHOTO);
    },
  });

  const onFinished = () => {
    toast.success(intl.formatMessage(messages.addPhotosSuccess));
    onRequestClose();
  };

  const uploader = async (file) => {
    try {
      let nodeId;

      if (secureUploadFlagEnabled && fileIsPhoto(file)) {
        const { data } = await uploadRequestAndCreateProjectPhoto(
          file,
          projectId,
        );

        nodeId = data.nodeId;
      } else {
        const data = await uploadFileAndCreateProjectImageV1(
          file,
          currentUser.Id,
          projectId,
        );

        nodeId = data.node_id;
      }

      await addTaskImages({
        variables: {
          input: {
            id: taskId,
            imageIds: [nodeId],
          },
        },
      });
      return true;
    } catch (err) {
      toast.error(err);
      return false;
    }
  };

  return (
    <StyledModal
      className="modal-contents"
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      isClosable
    >
      <h3 className="modal-title" htmlFor="taskListName">
        {intl.formatMessage(messages.uploadPhoto)}
      </h3>
      <DragAndDropUploader
        uploader={uploader}
        onFinishedCallback={onFinished}
        maxFileSizeBytes={Number(maxFileUploadSizeBytes)}
      />
    </StyledModal>
  );
};

TaskPhotoUploadModal.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  taskId: PropTypes.string,
};

export default TaskPhotoUploadModal;
